'use client';

import { useIntl } from 'react-intl';
import { cva } from 'class-variance-authority';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useCarousel } from './useCarousel';

export const navButtonVariants = cva(
  'bg-color-carousel-dot absolute top-1/2 z-30 size-5 -translate-y-1/2 rounded-full border-none shadow-lg',
  {
    variants: {
      variant: {
        default: '',
        wider: '-mx-1 lg:-mx-2 xl:-mx-3',
      },
      position: {
        left: 'left-1',
        right: 'right-1',
      },
    },
  },
);

export const CarouselNav = ({ variant = 'default' }: { variant?: 'default' | 'wider' }) => {
  const { atStart, atEnd, trackRef } = useCarousel();
  const { formatMessage } = useIntl();

  const scrollBySlides = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const firstChild = el.querySelector<HTMLElement>(':scope > *');
    if (!firstChild) return;
    const slideWidth =
      firstChild.getBoundingClientRect().width + parseFloat(getComputedStyle(el).columnGap || '0');
    // in the "CarouselList" component there are different configurations for visible items made with CSS variables
    // let's get the current one
    const itemsVisible = getComputedStyle(el).getPropertyValue('--items-visible');
    // now use the visible item count to calculate the scroll amount
    const count = parseInt(itemsVisible, 10);
    el.scrollBy({ left: dir * slideWidth * count, behavior: 'smooth' });
  };

  return (
    <>
      {!atStart && (
        <Button
          variant="icon-only"
          size="medium"
          aria-label={formatMessage({
            id: 'recoCarousel.aria.prevButton',
            defaultMessage: 'Vorheriges Element',
          })}
          className={navButtonVariants({ variant, position: 'left' })}
          onClick={() => scrollBySlides(-1)}
          tabIndex={-1}
        >
          <span className="icon-emp-arrow-medium-left size-4" role="img" />
        </Button>
      )}
      {!atEnd && (
        <Button
          variant="icon-only"
          size="medium"
          aria-label={formatMessage({
            id: 'recoCarousel.aria.nextButton',
            defaultMessage: 'Nächstes Element',
          })}
          className={navButtonVariants({ variant, position: 'right' })}
          onClick={() => scrollBySlides(1)}
          tabIndex={-1}
        >
          <span className="icon-emp-arrow-medium-right size-4" role="img" />
        </Button>
      )}
    </>
  );
};
